import React, { useState } from 'react';
import { Book } from './types';
import { featuredBooks, getBooksByGenre } from './data/books';
import { BookOpen, Zap, Search, Home } from 'lucide-react';
import QuizSection from './components/QuizSection';
import BookCard from './components/BookCard';
import BookCardSkeleton from './components/BookCardSkeleton';
import AffiliateDisclosure from './components/AffiliateDisclosure';
import AdUnit from './components/AdUnit';
import BackToTop from './components/BackToTop';
import FeedbackModal from './components/FeedbackModal';
import ThemeSwitcher from './components/ThemeSwitcher';
import BackgroundDecoration from './components/BackgroundDecoration';
import { getRecommendations } from './services/recommendations';
import { useTheme, getThemeClasses } from './context/ThemeContext';

function App() {
  const { theme } = useTheme();
  const themeClasses = getThemeClasses(theme);
  
  const [quizType, setQuizType] = useState<'quick' | 'comprehensive' | null>(null);
  const [recommendation, setRecommendation] = useState<Book | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [recommendations, setRecommendations] = useState<Book[]>([]);
  const [currentRecommendationIndex, setCurrentRecommendationIndex] = useState(0);
  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);

  const handleQuizComplete = async (results: any) => {
    setIsLoading(true);
    try {
      const newRecommendations = await getRecommendations(results);
      if (newRecommendations.length > 0) {
        setRecommendations(newRecommendations);
        setRecommendation(newRecommendations[0]);
        setCurrentRecommendationIndex(0);
      }
    } catch (error) {
      console.error('Error getting recommendations:', error);
      setRecommendations([featuredBooks.weeklyBook]);
      setRecommendation(featuredBooks.weeklyBook);
      setCurrentRecommendationIndex(0);
    } finally {
      setIsLoading(false);
      setQuizType(null);
    }
  };

  const handleNextRecommendation = () => {
    const nextIndex = currentRecommendationIndex + 1;
    if (nextIndex < recommendations.length) {
      setCurrentRecommendationIndex(nextIndex);
      setRecommendation(recommendations[nextIndex]);
    }
  };

  const handlePreviousRecommendation = () => {
    const prevIndex = currentRecommendationIndex - 1;
    if (prevIndex >= 0) {
      setCurrentRecommendationIndex(prevIndex);
      setRecommendation(recommendations[prevIndex]);
    }
  };

  const resetQuiz = () => {
    setQuizType(null);
    setRecommendation(null);
    setRecommendations([]);
    setCurrentRecommendationIndex(0);
  };

  const scrollToDisclosure = () => {
    const footer = document.querySelector('footer');
    if (footer) {
      footer.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className={`min-h-screen ${themeClasses.background} relative`}>
      <BackgroundDecoration />
      <header className={`${themeClasses.card} shadow-sm sticky top-0 z-10`}>
        <div className="max-w-7xl mx-auto px-3 py-2">
          <div className="flex justify-between items-center">
            <button
              onClick={resetQuiz}
              className={`flex items-center space-x-1.5 group hover:opacity-80 transition-opacity`}
            >
              <BookOpen className={`h-4 w-4 sm:h-5 sm:w-5 ${themeClasses.icon} group-hover:scale-110 transition-transform`} />
              <h1 className={`text-lg sm:text-xl font-bold ${themeClasses.text}`}>
                Book Bliss
              </h1>
            </button>
            <div className="flex items-center gap-4">
              <ThemeSwitcher />
              <button
                onClick={scrollToDisclosure}
                className={`text-xs ${themeClasses.textSecondary} hover:${themeClasses.text} flex items-center gap-1`}
              >
                Affiliate Disclosure
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-3 pt-4 sm:pt-6">
        {/* Quiz Selection */}
        {!quizType && !recommendation && (
          <div className="text-center mb-6 sm:mb-8">
            <h2 className={`text-xl sm:text-3xl font-bold ${themeClasses.text} mb-2 sm:mb-3`}>
              Discover Your Next Favorite Book
            </h2>
            <p className={`text-sm sm:text-base ${themeClasses.textSecondary} mb-4 sm:mb-8 max-w-2xl mx-auto`}>
              Choose your quiz type to get personalized book recommendations
            </p>
            <div className="grid grid-cols-2 gap-3 sm:gap-6 max-w-4xl mx-auto">
              <button
                onClick={() => setQuizType('quick')}
                className={`p-3 sm:p-6 ${themeClasses.card} rounded-xl shadow-sm hover:shadow-md transition-all duration-200
                         border-2 border-transparent hover:${themeClasses.border} text-left group`}
              >
                <div className="flex items-center mb-2 sm:mb-3">
                  <Zap className={`h-4 w-4 sm:h-6 sm:w-6 ${themeClasses.icon} mr-1.5 sm:mr-2 group-hover:scale-110 transition-transform`} />
                  <h3 className={`text-sm sm:text-xl font-bold ${themeClasses.text}`}>Quick Quiz</h3>
                </div>
                <p className={`text-xs sm:text-sm ${themeClasses.textSecondary} mb-1.5 sm:mb-2`}>
                  7 quick questions for instant recommendations
                </p>
                <p className={`text-xs sm:text-sm ${themeClasses.icon} font-semibold flex items-center`}>
                  Takes ~2 minutes
                  <span className="ml-1 group-hover:translate-x-1 transition-transform">→</span>
                </p>
              </button>
              
              <button
                onClick={() => setQuizType('comprehensive')}
                className={`p-3 sm:p-6 ${themeClasses.card} rounded-xl shadow-sm hover:shadow-md transition-all duration-200
                         border-2 border-transparent hover:${themeClasses.border} text-left group`}
              >
                <div className="flex items-center mb-2 sm:mb-3">
                  <Search className={`h-4 w-4 sm:h-6 sm:w-6 ${themeClasses.icon} mr-1.5 sm:mr-2 group-hover:scale-110 transition-transform`} />
                  <h3 className={`text-sm sm:text-xl font-bold ${themeClasses.text}`}>Detailed Quiz</h3>
                </div>
                <p className={`text-xs sm:text-sm ${themeClasses.textSecondary} mb-1.5 sm:mb-2`}>
                  A thorough exploration of your reading preferences
                </p>
                <p className={`text-xs sm:text-sm ${themeClasses.icon} font-semibold flex items-center`}>
                  Takes ~5 minutes
                  <span className="ml-1 group-hover:translate-x-1 transition-transform">→</span>
                </p>
              </button>
            </div>
          </div>
        )}

        {/* Quiz Section with transition */}
        <div className={`transition-opacity duration-300 ${quizType ? 'opacity-100' : 'opacity-0'}`}>
          {quizType && (
            <QuizSection 
              quizType={quizType} 
              onComplete={handleQuizComplete}
              onBack={resetQuiz}
            />
          )}
        </div>

        {/* Loading State with Skeleton */}
        {isLoading && (
          <div className="space-y-6">
            <BookCardSkeleton />
            <BookCardSkeleton />
            <BookCardSkeleton />
          </div>
        )}

        {/* Recommendation Section */}
        {recommendation && !isLoading && (
          <div className="mb-8 sm:mb-12">
            <h2 className={`text-xl sm:text-2xl font-bold ${themeClasses.text} mb-4 sm:mb-6`}>
              Based on your preferences, we recommend:
            </h2>
            <BookCard 
              book={recommendation}
              showNavigation={true}
              onNextBook={handleNextRecommendation}
              onPreviousBook={handlePreviousRecommendation}
              isFirstBook={currentRecommendationIndex === 0}
              isLastBook={currentRecommendationIndex === recommendations.length - 1}
            />
            <div className="mt-4 sm:mt-6 text-center">
              <button
                onClick={resetQuiz}
                className={`inline-flex items-center px-4 py-2 ${themeClasses.secondary} ${themeClasses.text} rounded-lg
                         hover:${themeClasses.secondaryHover} transition-colors duration-200 text-sm font-semibold`}
              >
                <Home className="mr-2 h-4 w-4" />
                Back to Home
              </button>
            </div>
          </div>
        )}

        {/* Featured Books Section */}
        {(!recommendation || !isLoading) && (
          <div className="space-y-8 sm:space-y-12">
            <div>
              <h2 className={`text-xl sm:text-2xl font-bold ${themeClasses.text} mb-4 sm:mb-6`}>
                Featured Book of the Week
              </h2>
              <BookCard book={featuredBooks.weeklyBook} featured showFeaturedLabel />
            </div>

            <div>
              <h2 className={`text-xl sm:text-2xl font-bold ${themeClasses.text} mb-4 sm:mb-6`}>
                I Am Currently Reading
              </h2>
              <BookCard book={featuredBooks.currentlyReading} featured />
            </div>
          </div>
        )}

        {/* Bottom Ad Unit */}
        <div className="mt-8 sm:mt-12 mb-4">
          <div className="hidden lg:block">
            <AdUnit slot="5432109876" format="horizontal" className="max-w-[970px]" />
          </div>
          <div className="hidden md:block lg:hidden">
            <AdUnit slot="5432109877" format="horizontal" className="max-w-[728px]" />
          </div>
          <div className="md:hidden">
            <AdUnit slot="5432109878" format="rectangle" className="max-w-[336px]" />
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className={`${themeClasses.card} border-t ${themeClasses.border}`}>
        <div className="max-w-7xl mx-auto px-3 py-4">
          <div className="flex flex-col items-center space-y-2">
            <AffiliateDisclosure />
            <p className={`text-xs ${themeClasses.textSecondary}`}>
              © 2024 Book Bliss
            </p>
          </div>
        </div>
      </footer>

      {/* Back to Top Button */}
      <BackToTop />

      {/* Feedback Modal */}
      <FeedbackModal
        isOpen={isFeedbackModalOpen}
        onClose={() => setIsFeedbackModalOpen(false)}
      />
    </div>
  );
}

export default App;